// Pixel Tracking Service
// Handles Facebook and TikTok Pixel initialization and event tracking

const API = process.env.REACT_APP_BACKEND_URL + '/api';

class PixelService {
  constructor() {
    this.initialized = false;
    this.pixels = [];
    this.facebookInitialized = false;
    this.tiktokInitialized = false;
    // Set of pixel IDs whose `fbq('init', ...)` / `ttq.load(...)` already ran.
    // Avoids multiple init calls (which causes pixel-helper to detect a pixel
    // on a page even when its product is not in scope).
    this.installedPixelIds = new Set();
    // Track which (pixelId|eventName|productId) tuples already fired this
    // session, to avoid duplicate events caused by React StrictMode or
    // re-renders triggering the same useEffect twice.
    this.firedEvents = new Set();
  }

  // Load pixels metadata from backend. Does NOT install any tracking yet —
  // installation is lazy (only when an event for an applicable pixel fires),
  // so pixel-helper won't detect pixels on products they don't apply to.
  async init() {
    if (this.initialized) return;

    try {
      const response = await fetch(`${API}/settings/pixels`);
      const data = await response.json();
      this.pixels = data.pixels || [];

      // Only load the JS base SDKs if at least one pixel is global (apply_to_all).
      // For per-product pixels we install lazily on demand.
      const hasGlobalFb = this.pixels.some(p => p.is_active && p.platform === 'facebook' && p.apply_to_all);
      const hasGlobalTt = this.pixels.some(p => p.is_active && p.platform === 'tiktok' && p.apply_to_all);

      if (hasGlobalFb) this.initFacebookBase();
      if (hasGlobalTt) this.initTikTokBase();

      // Install ONLY pixels marked as apply_to_all
      this.pixels.forEach(pixel => {
        if (!pixel.is_active) return;
        if (!pixel.apply_to_all) return;
        this.installPixel(pixel);
      });

      this.initialized = true;

      // Auto-track PageView for global pixels only (per-product is handled by trackViewContent)
      setTimeout(() => {
        this.trackPageView();
      }, 100);
    } catch (err) {
      console.error('PixelService init failed:', err?.message || err);
    }
  }

  // Lazily install a pixel (load base SDK if needed and call init/load).
  installPixel(pixel) {
    if (!pixel || !pixel.is_active) return;
    if (this.installedPixelIds.has(pixel.pixel_id)) return;

    if (pixel.platform === 'facebook') {
      this.initFacebookBase();
      this.initFacebookPixel(pixel.pixel_id);
    } else if (pixel.platform === 'tiktok') {
      this.initTikTokBase();
      this.initTikTokPixel(pixel.pixel_id);
    }
    this.installedPixelIds.add(pixel.pixel_id);
  }

  // Initialize Facebook Pixel base code
  initFacebookBase() {
    if (this.facebookInitialized || typeof window === 'undefined') return;
    
    // Facebook Pixel base code
    !function(f,b,e,v,n,t,s) {
      if(f.fbq) return;
      n = f.fbq = function() {
        n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments);
      };
      if(!f._fbq) f._fbq = n;
      n.push = n;
      n.loaded = !0;
      n.version = '2.0';
      n.queue = [];
      t = b.createElement(e);
      t.async = !0;
      t.src = v;
      s = b.getElementsByTagName(e)[0];
      s.parentNode.insertBefore(t, s);
    }(window, document, 'script', 'https://connect.facebook.net/en_US/fbevents.js');
    
    this.facebookInitialized = true;
  }

  // Initialize TikTok Pixel base code
  initTikTokBase() {
    if (this.tiktokInitialized || typeof window === 'undefined') return;
    
    // TikTok Pixel base code
    !function(w, d, t) {
      w.TiktokAnalyticsObject = t;
      const ttq = w[t] = w[t] || [];
      ttq.methods = ["page", "track", "identify", "instances", "debug", "on", "off", "once", "ready", "alias", "group", "enableCookie", "disableCookie", "holdConsent", "revokeConsent", "grantConsent"];
      ttq.setAndDefer = function(t, e) {
        t[e] = function() {
          t.push([e].concat(Array.prototype.slice.call(arguments, 0)));
        };
      };
      for (let i = 0; i < ttq.methods.length; i++) {
        ttq.setAndDefer(ttq, ttq.methods[i]);
      }
      ttq.instance = function(t) {
        const e = ttq._i[t] || [];
        for (let n = 0; n < ttq.methods.length; n++) {
          ttq.setAndDefer(e, ttq.methods[n]);
        }
        return e;
      };
      ttq.load = function(e, n) {
        const i = "https://analytics.tiktok.com/i18n/pixel/events.js";
        ttq._i = ttq._i || {};
        ttq._i[e] = [];
        ttq._i[e]._u = i;
        ttq._t = ttq._t || {};
        ttq._t[e] = +new Date();
        ttq._o = ttq._o || {};
        ttq._o[e] = n || {};
        const o = document.createElement("script");
        o.type = "text/javascript";
        o.async = !0;
        o.src = i + "?sdkid=" + e + "&lib=" + t;
        const a = document.getElementsByTagName("script")[0];
        a.parentNode.insertBefore(o, a);
      };
    }(window, document, 'ttq');
    
    this.tiktokInitialized = true;
  }

  // Initialize a specific Facebook Pixel
  initFacebookPixel(pixelId) {
    if (typeof window !== 'undefined' && window.fbq) {
      window.fbq('init', pixelId);
      //console.log('Facebook Pixel initialized:', pixelId);
    }
  }

  // Initialize a specific TikTok Pixel
  initTikTokPixel(pixelId) {
    if (typeof window !== 'undefined' && window.ttq) {
      window.ttq.load(pixelId);
      window.ttq.page();
      //console.log('TikTok Pixel initialized and page() called:', pixelId);
    } else {
      console.warn('TikTok ttq not available yet');
    }
  }

  // Get pixels that should track a specific product (NEW logic with apply_to_all flag)
  getPixelsForProduct(productId) {
    return this.pixels.filter(pixel => {
      if (!pixel.is_active) return false;
      // Global pixel: applies everywhere (including products added in the future)
      if (pixel.apply_to_all) return true;
      // Otherwise must be explicitly listed in product_ids
      const ids = pixel.product_ids || [];
      return productId && ids.includes(productId);
    });
  }

  // Check if a pixel should track a specific event
  shouldTrackEvent(pixel, eventName) {
    if (!pixel.events || pixel.events.length === 0) return true;
    return pixel.events.includes(eventName);
  }

  // De-duplicate events. Returns true if this is the FIRST time we fire
  // the given event in this session (and registers it). Returns false if
  // it's already been fired (so callers should skip).
  // This protects against React StrictMode double-execution and re-renders.
  _markFiredOnce(pixelId, eventName, productId, ttlMs = 0) {
    const key = `${pixelId}|${eventName}|${productId || 'global'}`;
    if (this.firedEvents.has(key)) return false;
    this.firedEvents.add(key);
    if (ttlMs > 0) {
      setTimeout(() => this.firedEvents.delete(key), ttlMs);
    }
    return true;
  }

  // Track PageView event
  trackPageView(productId = null) {
    const pixels = productId ? this.getPixelsForProduct(productId) : this.pixels.filter(p => p.is_active && p.apply_to_all);

    pixels.forEach(pixel => {
      if (!pixel.is_active) return;
      if (!this.shouldTrackEvent(pixel, 'PageView')) return;
      this.installPixel(pixel); // lazy install
      // PageView dedup uses a short TTL so SPA navigation still emits new PageViews
      if (!this._markFiredOnce(pixel.pixel_id, 'PageView', productId, 1500)) return;

      if (pixel.platform === 'facebook' && window.fbq) {
        window.fbq('track', 'PageView');
      } else if (pixel.platform === 'tiktok' && window.ttq) {
        window.ttq.page();
      }
    });
  }

  // Track ViewContent event
  trackViewContent(product) {
    const pixels = this.getPixelsForProduct(product.id);

    pixels.forEach(pixel => {
      if (!pixel.is_active) return;
      if (!this.shouldTrackEvent(pixel, 'ViewContent')) return;
      this.installPixel(pixel);
      if (!this._markFiredOnce(pixel.pixel_id, 'ViewContent', product.id)) return;

      if (pixel.platform === 'facebook' && window.fbq) {
        window.fbq('track', 'ViewContent', {
          content_type: 'product',
          content_ids: [product.id],
          content_name: product.name,
          value: product.price / 100,
          currency: 'BRL'
        });
      } else if (pixel.platform === 'tiktok' && window.ttq) {
        window.ttq.track('ViewContent', {
          content_id: product.id,
          content_type: 'product',
          content_name: product.name,
          value: product.price / 100,
          currency: 'BRL'
        });
      }
    });
  }

  // Track AddToCart event
  trackAddToCart(product, quantity = 1) {
    const pixels = this.getPixelsForProduct(product.id);

    pixels.forEach(pixel => {
      if (!pixel.is_active) return;
      if (!this.shouldTrackEvent(pixel, 'AddToCart')) return;
      this.installPixel(pixel);
      if (!this._markFiredOnce(pixel.pixel_id, 'AddToCart', product.id)) return;

      if (pixel.platform === 'facebook' && window.fbq) {
        window.fbq('track', 'AddToCart', {
          content_type: 'product',
          content_ids: [product.id],
          content_name: product.name,
          value: (product.price * quantity) / 100,
          currency: 'BRL',
          num_items: quantity
        });
      } else if (pixel.platform === 'tiktok' && window.ttq) {
        window.ttq.track('AddToCart', {
          content_id: product.id,
          content_type: 'product',
          content_name: product.name,
          value: (product.price * quantity) / 100,
          currency: 'BRL',
          quantity: quantity
        });
      }
    });
  }

  // Track InitiateCheckout event
  trackInitiateCheckout(product, quantity = 1) {
    const pixels = this.getPixelsForProduct(product.id);

    pixels.forEach(pixel => {
      if (!pixel.is_active) return;
      if (!this.shouldTrackEvent(pixel, 'InitiateCheckout')) return;
      this.installPixel(pixel);
      // CRITICAL: dedup InitiateCheckout to fix StrictMode double-fire
      if (!this._markFiredOnce(pixel.pixel_id, 'InitiateCheckout', product.id)) return;

      if (pixel.platform === 'facebook' && window.fbq) {
        window.fbq('track', 'InitiateCheckout', {
          content_type: 'product',
          content_ids: [product.id],
          content_name: product.name,
          value: (product.price * quantity) / 100,
          currency: 'BRL',
          num_items: quantity
        });
        //console.log('FB InitiateCheckout tracked:', product.name);
      } else if (pixel.platform === 'tiktok' && window.ttq) {
        window.ttq.track('InitiateCheckout', {
          content_id: product.id,
          content_type: 'product',
          content_name: product.name,
          value: (product.price * quantity) / 100,
          currency: 'BRL',
          quantity: quantity
        });
        //console.log('TT InitiateCheckout tracked:', product.name);
      }
    });
  }

  // Track Purchase event
  trackPurchase(product, quantity, totalValue, orderId) {
    const pixels = this.getPixelsForProduct(product.id);

    pixels.forEach(pixel => {
      if (!pixel.is_active) return;
      if (!this.shouldTrackEvent(pixel, 'Purchase')) return;
      this.installPixel(pixel);
      // Dedup by orderId so the same purchase can't fire twice
      if (!this._markFiredOnce(pixel.pixel_id, 'Purchase', `${product.id}:${orderId || 'no-id'}`)) return;

      if (pixel.platform === 'facebook' && window.fbq) {
        window.fbq('track', 'Purchase', {
          content_type: 'product',
          content_ids: [product.id],
          content_name: product.name,
          value: totalValue / 100,
          currency: 'BRL',
          num_items: quantity,
          order_id: orderId
        });
      } else if (pixel.platform === 'tiktok' && window.ttq) {
        window.ttq.track('CompletePayment', {
          content_id: product.id,
          content_type: 'product',
          content_name: product.name,
          value: totalValue / 100,
          currency: 'BRL',
          quantity: quantity,
          order_id: orderId
        });
      }
    });
  }

  // Track Lead event - global pixels only (no product context)
  trackLead(data = {}) {
    this.pixels.filter(p => p.is_active && p.apply_to_all).forEach(pixel => {
      if (!this.shouldTrackEvent(pixel, 'Lead')) return;
      this.installPixel(pixel);
      if (!this._markFiredOnce(pixel.pixel_id, 'Lead', 'global')) return;

      if (pixel.platform === 'facebook' && window.fbq) {
        window.fbq('track', 'Lead', data);
      } else if (pixel.platform === 'tiktok' && window.ttq) {
        window.ttq.track('SubmitForm', data);
      }
    });
  }
}

// Export singleton instance
const pixelService = new PixelService();
export default pixelService;
